import { useEffect } from 'react';
import { IWidgetContext } from '../../context/WidgetContext';
import useDHFlags from '../useDHFlags';
import useWidgetContext from '../useWidgetContext';
import normalizeConsentParams from '~/shared/utils/normalizeConsentParams';
import { useRecoilValue } from 'recoil';
import { consentSelector } from '../../state/widgetState';
import initializeGoogleAnalytics4 from './utils/initializeGoogleAnalytics4';

// GA cookie flags applied via gtag('config', { cookie_flags }) to analytics cookies.
// - max-age=7200: sets cookie lifetime to 2 hours (in seconds)
// - secure: cookie sent only over HTTPS (required when SameSite=None)
// - samesite=none: allow cross-site contexts (e.g., iframe/third-party)
// https://www.simoahava.com/analytics/cookieflags-field-google-analytics/
const COOKIE_FLAGS = 'max-age=7200;secure;samesite=none';

const VERSION = 'v1';

export const useDefaultEventOptions = () => {
  const flags = useDHFlags();
  const widgetContext = useWidgetContext();

  const anyFlagPotential = widgetContext.featureFlags.length !== 0;

  const version = anyFlagPotential
    ? flags.length !== 0
      ? `v3/${flags.map(({ name }) => name).join('+')}`
      : 'v3'
    : VERSION;

  const config = {
    cookie_flags: COOKIE_FLAGS,
    anonymize_ip: true,
    send_page_view: false,
    accountId: widgetContext.accountId,
  };

  return {
    ...config,
    version,
  };
};

const useSetupAnalytics = (widgetContext: IWidgetContext | null) => {
  const defaultConfigOptions = useDefaultEventOptions();
  const consentParams = useRecoilValue(consentSelector);

  // Update consent settings in gtag whenever consent changes
  useEffect(() => {
    if (consentParams) {
      const normalizedConsentParams = normalizeConsentParams(consentParams);
      gtag('consent', 'update', normalizedConsentParams);
    }
  }, [consentParams]);

  // Initialize GA tracking IDs when analytics consent is granted
  useEffect(() => {
    const normalizedConsentParams = normalizeConsentParams(consentParams);
    const isAnalyticsConsentGranted =
      normalizedConsentParams.analytics_storage === 'granted';

    const ownTrackingId = process.env.NEXT_PUBLIC_GA_ID;

    const initializeGA = () => {
      if (ownTrackingId) {
        initializeGoogleAnalytics4(ownTrackingId, defaultConfigOptions);
      }

      if (widgetContext?.googleAnalytics4) {
        initializeGoogleAnalytics4(
          widgetContext.googleAnalytics4,
          defaultConfigOptions,
        );
      }
    };

    // Initialize GA only when analytics consent is granted
    if (widgetContext !== null && isAnalyticsConsentGranted) {
      initializeGA();
    }
  }, [consentParams, widgetContext, defaultConfigOptions]);
};

export default useSetupAnalytics;
